import React, { useEffect, useRef, useState } from "react";
import "./TPMSNoHardware.css";

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   size:  "feature" = bada dark card | "wide" = do column wala | default = chhota
   extra: "radar" = pulsing rings | "rec" = blinking LIVE/RECORDED badge
   ============================================================ */
const HEAD = {
  badge: "SOFTWARE-ONLY APPROACH",
  title: "No Additional Roadside Hardware Required",
  subtitle: "Turn Existing Toll Cameras into an Intelligent Monitoring System",
  paragraphs: [
    "One of the key advantages of TPMS is its ability to work with compatible cameras already installed at toll plazas.",
    "There is no need to replace existing camera infrastructure or install additional lane-mounted detection sensors.",
    "TPMS analyzes existing camera video feeds to generate vehicle information and evidence.",
  ],
};

const ADVANTAGES_TITLE = "Key Advantages";

const ADVANTAGES = [
  {
    icon: "camera",
    size: "feature",
    extra: "radar",
    text: "Works with compatible existing toll plaza CCTV/IP cameras",
  },
  { icon: "ban", text: "No additional vehicle-detection sensors required" },
  { icon: "road", text: "No modification to toll barriers or lane infrastructure" },
  { icon: "layers", size: "wide", tone: "gradient", text: "Supports multiple camera feeds and toll lanes" },
  { icon: "play", extra: "rec", text: "Can analyze live and previously recorded video" },
  {
    icon: "wrench",
    size: "wide",
    tone: "tint",
    text: "Reduces additional roadside installation and maintenance requirements",
  },
  { icon: "cloud", text: "Integrates with suitable on-premises or cloud processing infrastructure" },
];

const CLOSING = {
  line1: "Leverage Your Existing Infrastructure.",
  line2: "Add Intelligence Through Software.",
};

/* flow diagram (right panel) */
const FLOW = [
  { icon: "camera", title: "Existing Toll Cameras", text: "Compatible CCTV / IP cameras" },
  { icon: "cpu", title: "TPMS Software", text: "Analyzes live & recorded video" },
  { icon: "file", title: "Vehicle Information & Evidence", text: "Records, images and video" },
];

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  camera: (
    <>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  ban: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </>
  ),
  road: <path d="M4 19 8 5M20 19 16 5M12 6v2M12 11v2M12 16v2" />,
  layers: (
    <>
      <path d="m12 2 10 5-10 5L2 7z" />
      <path d="m2 17 10 5 10-5" />
      <path d="m2 12 10 5 10-5" />
    </>
  ),
  play: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m10 8 6 4-6 4z" />
    </>
  ),
  wrench: (
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  ),
  cloud: <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />,
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  file: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="m9 15 2 2 4-4" />
    </>
  ),
  down: <path d="M12 5v14m0 0-6-6m6 6 6-6" />,
};

const Icon = ({ name, size = 26 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* scroll par ek baar "visible" ho jaye to true return karta hai */
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, visible];
};

/* ============================================================
   SECTION
   ============================================================ */
const TPMSNoHardware = () => {
  const [advRef, advVisible] = useInView(0.15);

  return (
    <section id="no-hardware" className="tn">
      <div className="tn__container">
        {/* ---------- heading ---------- */}
        <header className="tn__head">
          <span className="tn__pill">{HEAD.badge}</span>
          <h2 className="tn__title">{HEAD.title}</h2>
          <p className="tn__subtitle">{HEAD.subtitle}</p>
          <span className="tn__underline" />
        </header>

        {/* ---------- text + diagram ---------- */}
        <div className="tn__split">
          <div className="tn__text">
            {HEAD.paragraphs.map((p, i) => (
              <p key={p} className={i === HEAD.paragraphs.length - 1 ? "is-strong" : ""}>
                {p}
              </p>
            ))}
          </div>

          <div className="tn__panel" aria-hidden="true">
            <span className="tn__tag">SOFTWARE ONLY</span>
            {FLOW.map((f, i) => (
              <React.Fragment key={f.title}>
                <div className="tn__node">
                  <span className="tn__node-icon">
                    <Icon name={f.icon} />
                  </span>
                  <div>
                    <strong>{f.title}</strong>
                    <span>{f.text}</span>
                  </div>
                </div>
                {i < FLOW.length - 1 && (
                  <span className="tn__arrow">
                    <Icon name="down" size={18} />
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ---------- key advantages (bento + animation) ---------- */}
        <div className="tn__adv-head">
          <h3>{ADVANTAGES_TITLE}</h3>
          <span className="tn__underline tn__underline--sm" />
        </div>

        <ul ref={advRef} className={`tn__bento ${advVisible ? "is-visible" : ""}`}>
          {ADVANTAGES.map((a, i) => (
            <li
              key={a.text}
              style={{ "--i": i }}
              className={[
                "tn__b",
                a.size ? `is-${a.size}` : "",
                a.tone ? `tone-${a.tone}` : "",
              ].join(" ")}
            >
              <span className="tn__b-num" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>

              {a.extra === "radar" && (
                <span className="tn__radar" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              )}

              <span className="tn__b-icon">
                <Icon name={a.icon} size={a.size === "feature" ? 34 : 26} />
              </span>

              {a.extra === "rec" && (
                <span className="tn__rec" aria-hidden="true">
                  <i /> LIVE / RECORDED
                </span>
              )}

              <p className="tn__b-text">{a.text}</p>
              <span className="tn__b-bar" aria-hidden="true" />
            </li>
          ))}
        </ul>

        {/* ---------- closing banner ---------- */}
        <div className="tn__closing">
          <p>
            {CLOSING.line1} <span>{CLOSING.line2}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default TPMSNoHardware;